/**
 * useRoleGuard - Check if user has required role
 * Mirrors legacy app RoleGuard
 *
 * Checks:
 * - User has role specified in route.data.checkRoleFor
 * - For external users, validates full authorization
 */

import { GuardResult, RouteConfig } from '../types';

const ADVOCATE_ALLOWED_TYPES = [
  'UHCI_General',
  'UHCI_General_and_Executive',
  'General_and_Network_Management',
  'UHCI_Project',
  'UHCI_Advocate',
  'UHCI_Executive',
  'UHCI_Admin',
];

const ROLE_GROUPS: Record<string, string[]> = {
  UHCI_SCORE_CARD: [
    'General_and_Network_Management',
    'UHCI_Project',
    'UHCI_Admin',
    'GeneralPlus_ProviderExperienceMeasurement',
  ],
  UHCI_ADM: [
    'General_and_Network_Management',
    'UHCI_Project',
    'UHCI_Admin',
    'GeneralPlus_ProviderExperienceMeasurement',
  ],
  UHCI_PEM: ['GeneralPlus_ProviderExperienceMeasurement', 'UHCI_Admin', 'UHCI_Project'],
};

function getUserRoles(user: any): string[] {
  const userData = Array.isArray(user) ? user[0] : user;
  const personas = userData?.UserPersonas || [];
  const personaRoles = personas.map((p: any) => p?.UserRole || p?.UserType).filter(Boolean);
  const directRoles = userData?.roles || userData?.Roles || [];
  return [...personaRoles, ...directRoles];
}

function hasAnyRole(userRoles: string[], allowedRoles: string[]): boolean {
  return allowedRoles.some(r => userRoles.includes(r));
}

export interface RoleAuthState {
  authenticated: boolean;
  hasRole: (role: string) => boolean;
  user: any;
  config: { isInternal: boolean } | null;
}

export function checkRoleGuard(route: RouteConfig, auth: RoleAuthState): GuardResult {
  const { authenticated, hasRole, user, config } = auth;

  // Must be authenticated first
  if (!authenticated) {
    return { allowed: false, redirect: '/login' };
  }

  // If route specifies required role, check it
  const requiredRole = route.data?.checkRoleFor;
  const userRoles = getUserRoles(user);

  if (requiredRole) {
    let hasAccess = false;

    if (requiredRole === 'UHCI_Advocate' || requiredRole === 'UHCI_Project') {
      hasAccess = hasAnyRole(userRoles, ADVOCATE_ALLOWED_TYPES);
    } else if (requiredRole === 'UHCI_ADM' || requiredRole === 'UHCI_SCORE_CARD' || requiredRole === 'UHCI_PEM') {
      hasAccess = hasAnyRole(userRoles, ROLE_GROUPS[requiredRole] || []);
    } else if (requiredRole === 'UHCI_Admin') {
      hasAccess = userRoles.includes('UHCI_Admin');
    } else {
      hasAccess = hasRole(requiredRole);
    }

    if (!hasAccess) {
      console.log(`X RoleGuard - User missing role access: ${requiredRole}`);
      return { allowed: false, redirect: '/AccessDenied', error: `Missing role access: ${requiredRole}` };
    }
  }

  // External users - ensure full authorization data
  if (!config?.isInternal) {
    const hasProviders = user?.Providers?.length > 0;
    const hasOptumId = user?.OptumId;
    const hasOrgName = user?.HealthCareOrganizationName;

    if (!hasProviders && !hasOptumId && !hasOrgName) {
      console.log('X RoleGuard - External user missing authorization data');
      return { allowed: false, redirect: '/login', error: 'Missing authorization data' };
    }
  }

  return { allowed: true };
}

